import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { SITE } from "@/config";
import { BuyButton } from "@/components/BuyButton";
import { cn } from "@/lib/utils";

const NAV = [
  { label: "How It Works", id: "how-it-works" },
  { label: "Benefits", id: "benefits" },
  { label: "FAQ", id: "faq" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const scroll = () =>
      window.lenis
        ? window.lenis.scrollTo(`#${id}`, { offset: -80 })
        : document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scroll, 350);
    } else {
      scroll();
    }
  };

  return (
    <header
      data-testid="site-header"
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300",
        scrolled
          ? "bg-sand/70 backdrop-blur-xl shadow-[0_2px_20px_rgba(0,0,0,0.05)]"
          : "bg-transparent"
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 md:h-20 md:px-12">
        <Link
          to="/"
          data-testid="brand-logo"
          className="font-display text-lg font-semibold tracking-[0.14em] text-ink"
          onClick={() => window.lenis?.scrollTo(0)}
        >
          {SITE.brand}
        </Link>

        <nav className="hidden items-center gap-9 md:flex">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => go(n.id)}
              data-testid={`nav-${n.id}`}
              className="text-sm font-medium text-graphite transition-colors duration-200 hover:text-ink"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <BuyButton
            testId="buy-now-header"
            className="px-5 py-2.5 text-sm"
          >
            Buy now
          </BuyButton>
          <button
            className="ml-1 inline-flex h-10 w-10 items-center justify-center rounded-full text-ink md:hidden"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="border-t border-line bg-sand/95 backdrop-blur-xl md:hidden"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col px-6 py-4">
            {NAV.map((n) => (
              <button
                key={n.id}
                onClick={() => go(n.id)}
                className="py-3 text-left text-base font-medium text-ink"
                data-testid={`mobile-nav-${n.id}`}
              >
                {n.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
